/**
 * The Browse table's view state as one human-readable, round-trippable query
 * string — the text in the bar above the table and the payload of `?q=`.
 *
 *   Tab = "People"  AND  (Name contains "ali"  OR  Team name = "Ops")
 *   ORDER BY Name ASC  SHOW "Name", "Description"
 *
 * The contract is `parse(build(state)) === state`: a link reproduces the exact
 * *query*, never the results. Rows are still fetched and filtered per viewer,
 * and the Description clause re-runs the hybrid search on the reader's node
 * rather than carrying a frozen id list.
 *
 * ## Shape of a filter
 *
 * Filters are disjunctive normal form: `groups` is a list of OR branches, each
 * an AND of per-column clauses. `[{}]` is "no filter". A single group is the
 * plain AND query the column dropdowns produce; more than one can only come
 * from typing, since a column dropdown has no way to express a relationship
 * with a *different* column.
 *
 * Two levels of OR share the keyword and are told apart by structure, not
 * spacing: a piece with no operator (`"Things"`) continues the previous
 * clause's value list, a piece with one starts a new branch.
 *
 *   Type = "People" OR "Things"        -> one branch, one clause, two values
 *   Type = "People" OR Name contains "a" -> two branches
 *
 * ## Tab
 *
 * `Tab` is not an operand of the expression — it chooses which rows exist at
 * all (Admins and Unapproved aren't entity filters but different sources), so
 * it's always an outer AND. A Tab clause written inside a branch is hoisted
 * out, and re-rendering shows it hoisted.
 *
 * Columns are addressed by their human title rather than their internal key so
 * the string stays readable and the bar and the URL can be the same text. A
 * title the reader's node has no column for comes back in `unresolved` instead
 * of being silently dropped.
 *
 * Verbs carry the difference between the two kinds of text filter: `contains`
 * is a literal substring test, `means` is the hybrid (vector + BM25) search,
 * which is fuzzy, tolerates typos, finds related wording — and also reads the
 * entity's name, ranking a name hit above the same word in a description. So
 * `Description means "…"` isn't literally true, but it names the one clause
 * that thinks rather than matches. `matches` is accepted as a synonym, since
 * queries shared before the rename spell it that way.
 */

const TAB_TITLE = 'Tab';
const AND = '  AND  ';
const OR_GROUP = '  OR  ';
const OR_VALUE = ' OR ';
const SORT_SEP = ', ';
// The Description column filters through /api/entities/search (vector + text),
// every other free-text column is a literal substring test — the verb says which.
const HYBRID_KEY = 'description';
// antd has no notion of "no value", so the person-field columns synthesise one.
const EMPTY_LABEL = '(empty)';
const EMPTY_VALUE = '__empty__';
// `means` is the current spelling of the hybrid clause; `matches` is its
// retired synonym, still parsed so older shared links resolve.
const HYBRID_VERB = 'means';
const OPERATORS = ['=', 'contains', HYBRID_VERB, 'matches'];

const quote = v => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

function unquote(text) {
    const t = text.trim();
    if (t.length < 2 || !t.startsWith('"') || !t.endsWith('"')) return null;
    return t.slice(1, -1).replace(/\\(.)/g, '$1');
}

/**
 * Offsets of `keyword` where it stands as its own word, outside quotes and at
 * paren depth 0. Every structural search goes through here: a filter value may
 * itself contain "AND", "ORDER BY" or a stray bracket, and a parenthesised
 * branch's internals must stay invisible to the level above it.
 */
function topLevelSpans(text, keyword) {
    const spans = [];
    let inQuotes = false;
    let depth = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '\\') i++;
            else if (ch === '"') inQuotes = false;
            continue;
        }
        if (ch === '"') { inQuotes = true; continue; }
        if (ch === '(') { depth++; continue; }
        if (ch === ')') { depth = Math.max(0, depth - 1); continue; }
        // Case-insensitive: people type `name contains` and `or`, and the bar
        // re-renders whatever they typed in canonical case.
        if (depth !== 0 || text.slice(i, i + keyword.length).toLowerCase() !== keyword.toLowerCase()) continue;
        const before = text[i - 1];
        const after = text[i + keyword.length];
        if ((before !== undefined && !/\s/.test(before)) || (after !== undefined && !/\s/.test(after))) continue;
        spans.push([i, i + keyword.length]);
        i += keyword.length - 1;
    }
    return spans;
}

function splitOn(text, keyword) {
    const spans = topLevelSpans(text, keyword);
    if (spans.length === 0) return [text];
    const parts = [];
    let start = 0;
    for (const [from, to] of spans) { parts.push(text.slice(start, from)); start = to; }
    parts.push(text.slice(start));
    return parts;
}

// Commas aren't words, so the boundary rule above can't find them: lists split
// with this instead, still skipping anything quoted.
function splitList(text) {
    const parts = [];
    let inQuotes = false;
    let start = 0;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '\\') i++;
            else if (ch === '"') inQuotes = false;
            continue;
        }
        if (ch === '"') inQuotes = true;
        else if (ch === ',') { parts.push(text.slice(start, i)); start = i + 1; }
    }
    parts.push(text.slice(start));
    return parts;
}

// Splits off the first top-level `keyword` section, e.g. everything after SHOW.
function takeSection(text, keyword) {
    const spans = topLevelSpans(text, keyword);
    if (spans.length === 0) return [text, null];
    return [text.slice(0, spans[0][0]), text.slice(spans[0][1])];
}

/**
 * `"a"` or `"a" OR "b"` — the value list on the right of an operator — or null
 * if the text is anything else. Strict on purpose: it's how a clause is told
 * from a boundary, so `"a" AND Type = "b"` must fail rather than swallow the
 * rest of the query as one long value.
 */
function parseValueList(text) {
    const values = [];
    let i = 0;
    const skipSpace = () => { while (i < text.length && /\s/.test(text[i])) i++; };
    for (;;) {
        skipSpace();
        if (text[i] !== '"') return null;
        let value = '';
        for (i++; i < text.length && text[i] !== '"'; i++) {
            if (text[i] === '\\') { value += text[i + 1] ?? ''; i++; continue; }
            value += text[i];
        }
        if (text[i] !== '"') return null; // unterminated
        values.push(value);
        i++;
        skipSpace();
        if (i >= text.length) return values;
        if (text.slice(i, i + 2).toLowerCase() !== 'or') return null;
        i += 2;
        if (i < text.length && !/\s/.test(text[i])) return null;
    }
}

function findOperator(clause) {
    return OPERATORS
        .map(op => ({ op, at: topLevelSpans(clause, op)[0] }))
        .filter(({ at }) => at && at[0] > 0)
        .sort((a, b) => a.at[0] - b.at[0])[0] ?? null;
}

function stripParens(text) {
    let t = text.trim();
    // Only strip when the leading paren is the one the trailing paren closes —
    // "(a)  OR  (b)" has to survive intact.
    while (t.startsWith('(') && t.endsWith(')')) {
        let depth = 0;
        let wraps = true;
        for (let i = 0; i < t.length && wraps; i++) {
            if (t[i] === '(') depth++;
            else if (t[i] === ')' && --depth === 0 && i !== t.length - 1) wraps = false;
        }
        if (!wraps) break;
        t = t.slice(1, -1).trim();
    }
    return t;
}

const labelFor = (col, value) => col.filters?.find(f => f.value === value)?.text
    ?? (value === EMPTY_VALUE ? EMPTY_LABEL : String(value));

// The reverse: a node that hasn't loaded its rows yet has no `filters` options
// to look a label up in, so fall back to the label itself — the person-field
// columns use the value as its own text, which is the only case that matters.
const valueFor = (col, label) => col.filters?.find(f => String(f.text).toLowerCase() === String(label).toLowerCase())?.value
    ?? (String(label).toLowerCase() === EMPTY_LABEL ? EMPTY_VALUE : label);

const clauseText = (col, values) => (col.filters
    ? `${col.title} = ${values.map(v => quote(labelFor(col, v))).join(OR_VALUE)}`
    : `${col.title} ${col.key === HYBRID_KEY ? HYBRID_VERB : 'contains'} ${quote(values[0])}`);

/**
 * Whether `text` is written in this grammar at all, rather than being a plain
 * phrase someone typed to search for. Lets one box be both a search field and a
 * query language: anything with no operator or section keyword in it is just
 * words, and the caller turns it into whatever its default search clause is.
 */
export const isQueryExpression = text => [...OPERATORS, 'ORDER BY', 'SHOW']
    .some(keyword => topLevelSpans(String(text || ''), keyword).length > 0);

/** True when any branch constrains anything — i.e. there is a query to clear. */
export const hasFilterClauses = (groups = []) =>
    groups.some(g => Object.values(g).some(v => Array.isArray(v) && v.length > 0));

/**
 * @param columns       the table's column defs, in display order
 * @param groups        OR branches, each { [columnKey]: value[] }
 * @param sorters       { [columnKey]: 'ascend' | 'descend' }
 * @param tabLabel      label of the active entity-type tab, or null for "All"
 * @param visibleTitles column titles to pin, or null to leave columns as-is
 */
export function buildBrowseQuery({ columns, groups = [{}], sorters = {}, tabLabel = null, visibleTitles = null }) {
    // Clause order follows the columns, so the same state is always the same
    // string — which is what makes the round trip stable.
    const branches = groups
        .map(group => columns
            .filter(col => Array.isArray(group[col.key]) && group[col.key].length > 0)
            .map(col => clauseText(col, group[col.key]))
            .join(AND))
        .filter(Boolean);

    let expression = branches.length === 1
        ? branches[0]
        : branches.map(b => (topLevelSpans(b, 'AND').length > 0 ? `(${b})` : b)).join(OR_GROUP);
    if (tabLabel) {
        // The tab applies to every branch, so an OR expression has to be
        // bracketed under it rather than reading as one more alternative.
        const scoped = branches.length > 1 ? `(${expression})` : expression;
        expression = scoped ? `${TAB_TITLE} = ${quote(tabLabel)}${AND}${scoped}` : `${TAB_TITLE} = ${quote(tabLabel)}`;
    }

    const sorts = columns
        .filter(col => sorters[col.key])
        .map(col => `${col.title} ${sorters[col.key] === 'ascend' ? 'ASC' : 'DESC'}`);
    return [
        expression,
        sorts.length > 0 ? `ORDER BY ${sorts.join(SORT_SEP)}` : '',
        visibleTitles ? `SHOW ${visibleTitles.map(quote).join(SORT_SEP)}` : '',
    ].filter(Boolean).join('  ');
}

/**
 * @returns { tab, groups, sorters, visibleKeys, unresolved } — `tab` and
 * `visibleKeys` are null when the query didn't mention them (leave as-is), and
 * `unresolved` lists titles this node has no column for.
 */
export function parseBrowseQuery(text, { columns = [], tabs = [] } = {}) {
    const result = { tab: null, groups: [{}], sorters: {}, visibleKeys: null, unresolved: [] };
    const sameName = (a, b) => String(a).trim().toLowerCase().replace(/\s+/g, ' ')
        === String(b).trim().toLowerCase().replace(/\s+/g, ' ');
    const byTitle = title => columns.find(col => sameName(col.title, title));

    /**
     * Read `text` as one complete clause, or null. Tried before every
     * structural split, so a column whose own title contains "and" or "or"
     * ("Skills and interests") is read as itself rather than torn in half by
     * its own name — which case-insensitive keywords make far more likely.
     */
    const asClause = (text) => {
        const clause = text.trim();
        const op = findOperator(clause);
        if (!op) return null;
        // A title is a plain name. Quotes or brackets in it mean the operator
        // found belongs to a later clause and this text spans a boundary —
        // `(Status = "x")  OR  Name contains "z"` is not one clause called
        // `(Status = "x")  OR  Name`. A title containing the *word* "and" is
        // fine, which is the whole reason this check isn't about keywords.
        const title = clause.slice(0, op.at[0]).trim();
        if (!title || /["()]/.test(title)) return null;
        const values = parseValueList(clause.slice(op.at[1]));
        if (values === null || values.length === 0) return null;
        return { title, values };
    };

    let rest = String(text || '').trim();
    // Right to left, so a stripped section can't hide the one before it.
    let showSection = null;
    let orderSection = null;
    [rest, showSection] = takeSection(rest, 'SHOW');
    [rest, orderSection] = takeSection(rest, 'ORDER BY');

    // Tab is an outer AND wherever it was written, so lift it out before the
    // expression is parsed — what's left is pure DNF.
    const keep = [];
    for (const part of (asClause(rest) ? [rest] : splitOn(rest, 'AND'))) {
        const clause = asClause(part);
        if (!clause || !sameName(clause.title, TAB_TITLE)) { keep.push(part); continue; }
        const tab = tabs.find(t => sameName(t.label, clause.values[0]));
        if (tab) result.tab = tab.value; else result.unresolved.push(clause.values[0]);
    }
    rest = keep.join(AND).trim();

    // A clause that names a column this node really has.
    const resolves = (raw) => {
        const clause = asClause(raw);
        return !!(clause && byTitle(clause.title));
    };

    /**
     * AND-joined clauses. Splitting on the keyword alone isn't enough: a column
     * title may contain the word "and" ("Skills and interests"), and the split
     * has no way to know. So a part that doesn't resolve pulls in the ones after
     * it until it does — and falls back to itself when no join helps, so an
     * unknown column is still reported as that column rather than as a run of
     * text that happened to follow it.
     */
    const readGroup = (source) => {
        const group = {};
        const missing = [];
        const parts = splitOn(source, 'AND');
        for (let i = 0; i < parts.length; i++) {
            let joined = parts[i];
            let j = i;
            while (j + 1 < parts.length && !resolves(joined)) joined += AND + parts[++j];
            if (resolves(joined)) i = j; else joined = parts[i];
            if (!joined.trim()) continue;
            const clause = asClause(joined);
            const col = clause && byTitle(clause.title);
            if (col) group[col.key] = col.filters ? clause.values.map(l => valueFor(col, l)) : [clause.values[0]];
            else missing.push(clause ? clause.title : joined.trim());
        }
        return { group, missing, ok: missing.length === 0 && Object.keys(group).length > 0 };
    };

    const takeGroup = (source) => {
        const { group, missing } = readGroup(source);
        result.unresolved.push(...missing);
        return group;
    };

    const parseExpression = (source, depth = 0) => {
        // A piece that *opens* with a bare quoted value continues the previous
        // clause's value list rather than starting a new branch — that's what
        // tells `= "a" OR "b"` from `= "a" OR Name contains "b"`, whatever the
        // spacing. It has to be the opening segment, not the whole piece: in
        // `= "a" OR "b" AND Name contains "c"` the value is only the head, and
        // everything after the AND belongs to that same branch.
        const continues = piece => unquote(splitOn(piece, 'AND')[0]) !== null;
        // A clause that reads whole is whole — never split it looking for more.
        if (resolves(source)) return [takeGroup(source)];
        const branches = [];
        for (const piece of splitOn(source, 'OR')) {
            const prev = branches[branches.length - 1];
            // Merge when the piece is a value continuation, or when merging is
            // what makes the branch parse — the "or" was inside a title ("Work
            // or study"). Requiring it to actually help keeps a genuinely
            // unknown column reported as itself.
            const merge = prev !== undefined
                && (continues(piece) || (!readGroup(prev).ok && readGroup(prev + OR_VALUE + piece).ok));
            if (merge) branches[branches.length - 1] = prev + OR_VALUE + piece;
            else branches.push(piece);
        }
        return branches.flatMap(branch => {
            const inner = stripParens(branch);
            // Parens may hold a nested OR; flatten it into the same DNF list.
            if (inner !== branch.trim() && depth < 8) return parseExpression(inner, depth + 1);
            return [takeGroup(inner)];
        });
    };

    if (rest) {
        const groups = parseExpression(rest).filter(g => Object.keys(g).length > 0);
        if (groups.length > 0) result.groups = groups;
    }

    for (const raw of splitList(orderSection || '')) {
        const match = /^(.*?)\s+(ASC|DESC)$/i.exec(raw.trim());
        if (!match) continue;
        const col = byTitle(match[1]);
        if (!col) { result.unresolved.push(match[1]); continue; }
        result.sorters[col.key] = match[2].toUpperCase() === 'ASC' ? 'ascend' : 'descend';
    }

    if (showSection !== null) {
        result.visibleKeys = [];
        for (const raw of splitList(showSection)) {
            const title = unquote(raw);
            if (title === null) continue;
            const col = byTitle(title);
            if (col) result.visibleKeys.push(col.key); else result.unresolved.push(title);
        }
    }
    return result;
}

/**
 * Evaluate the DNF filter against one row. `predicate(columnKey, values, row)`
 * answers a single clause — an unknown column lets the row through, so a query
 * naming something this node lacks never silently empties the table.
 *
 * antd can't do this itself: its per-column `onFilter` only ever sees one
 * column's criteria, so it can only AND them. Owning the evaluation here is
 * what buys cross-column OR.
 */
export function evaluateBrowseFilter(row, groups, predicate) {
    if (!Array.isArray(groups) || groups.length === 0) return true;
    return groups.some(group => Object.entries(group)
        .filter(([, values]) => Array.isArray(values) && values.length > 0)
        .every(([key, values]) => predicate(key, values, row)));
}

/** Every distinct value filtered on for one column, across all branches. */
export const valuesForColumn = (groups = [], key) =>
    [...new Set(groups.flatMap(g => (Array.isArray(g[key]) ? g[key] : [])))];
